import { gql, useQuery } from '@apollo/client';
import { DirectoryPhysician } from 'app/containers/ActForm/DoctorAutocomplete/DoctorAutocomplete';
import Spinner from 'application/components/spinner';
import Icon from 'application/components/icon';
import React, { useState } from 'react';
import { DirectoryPhysicianType } from 'shared/medyx-core/types/directory-physician';
import RalphApi from 'application/services/ralph-api';
import useToast from 'application/hooks/use-toast';
import moment from 'moment';

const GET_LATEST_DIRECTORY_PHYSICIANS_QUERY = gql`
  query LatestDirectoryPhysicians($type: DirectoryPhysicianType) {
    directoryPhysicians(type: $type, offset: 0, sortKey: "updatedAt", sortDirection: "desc") {
      practiceNumber
      firstName
      lastName
      updatedAt
      specialties {
        label
      }
    }
  }
`;

interface DataTableProps {
  data?: DirectoryPhysician[];
  loading: boolean;
  type: DirectoryPhysicianType;
  onAdd: (practiceNumber: string) => Promise<void>;
}

const DataTable: React.FunctionComponent<DataTableProps> = ({ data, loading, type, onAdd }) => {
  const [practiceNumber, setPracticeNumber] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const { addToast } = useToast();
  const physicians = data ?? [];

  const onSubmit = async () => {
    setIsAdding(true);
    onAdd(practiceNumber)
      .then(() => {
        setPracticeNumber('');
        addToast({
          message: `Le ${type === DirectoryPhysicianType.PHYSICIAN ? 'médecin' : 'résident'} a été ajouté avec succès`,
          type: 'success',
          autoDismiss: true
        });
      })
      .catch((error) => {
        addToast({
          message: error.response?.data?.error ?? error.message ?? 'Une erreur est survenue',
          type: 'error',
          autoDismiss: true
        });
      })
      .finally(() => {
        setIsAdding(false);
      });
  };

  return (
    <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-sm">
      {loading && (
        <div className="absolute inset-0 z-[5] flex flex-col items-center justify-center gap-2 bg-gray-100">
          <Spinner />
          <span className="text-gray-500">Chargement des données...</span>
        </div>
      )}
      <table className="w-full border-collapse">
        <caption className="sticky top-0 z-20 border-b-2 border-gray-300 bg-gray-100 px-6 py-2 text-left align-middle text-xl font-bold">
          <div className="text-xl font-bold">
            {`${type === DirectoryPhysicianType.PHYSICIAN ? 'Médecins' : 'Résidents'} récents (Bottin Medyx)`}
          </div>
        </caption>
        <thead className="sticky top-[45px] z-10 border-gray-300 bg-gray-100">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Prénom</th>
            <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">Nom</th>
            <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
              Numéro de pratique
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
              Spécialités
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
              Dernière mise à jour
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {!loading &&
            physicians.map((physician, index) => (
              <tr
                key={`${physician.practiceNumber}-${index}`}
                className={`transition-colors duration-150 ${
                  index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">{physician.lastName}</td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">{physician.firstName}</td>
                <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                  {physician.practiceNumber}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {physician.specialties && physician.specialties.length > 0 ? (
                    physician.specialties.map((s) => s.label).join(', ')
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                  {physician.updatedAt ? moment(physician.updatedAt).format('YYYY-MM-DD HH:mm') : '-'}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};

const CmqDirectoryPage: React.FunctionComponent = () => {
  const {
    data: physiciansData,
    loading: physiciansLoading,
    refetch: refetchPhysicians
  } = useQuery<{ directoryPhysicians: DirectoryPhysician[] }>(GET_LATEST_DIRECTORY_PHYSICIANS_QUERY, {
    variables: { type: DirectoryPhysicianType.PHYSICIAN }
  });

  const {
    data: residentsData,
    loading: residentsLoading,
    refetch: refetchResidents
  } = useQuery<{ directoryPhysicians: DirectoryPhysician[] }>(GET_LATEST_DIRECTORY_PHYSICIANS_QUERY, {
    variables: { type: DirectoryPhysicianType.RESIDENT }
  });

  const addPhysician = async (practiceNumber: string) => {
    if (practiceNumber.length !== 5) {
      throw new Error('Le numéro de pratique doit contenir 5 chiffres');
    }

    return RalphApi.request({
      endpoint: 'directory-physicians/synchronize',
      method: 'POST',
      body: { practiceNumber }
    }).then(() => {
      refetchPhysicians();
    });
  };

  const addResident = async (practiceNumber: string) => {
    const practiceNumberRegex = /^R\d{5}$/;
    if (!practiceNumberRegex.test(practiceNumber)) {
      throw new Error('Le numéro de résident doit être au format R#####');
    }

    return RalphApi.request({
      endpoint: 'directory-physicians/resident-synchronize',
      method: 'POST',
      body: { practiceNumber }
    }).then(() => {
      refetchResidents();
    });
  };

  return (
    <div className="flex h-[90vh] flex-col gap-4 p-5">
      <DataTable
        data={physiciansData?.directoryPhysicians}
        loading={physiciansLoading}
        type={DirectoryPhysicianType.PHYSICIAN}
        onAdd={addPhysician}
      />
      <DataTable
        data={residentsData?.directoryPhysicians}
        loading={residentsLoading}
        type={DirectoryPhysicianType.RESIDENT}
        onAdd={addResident}
      />
    </div>
  );
};

export default CmqDirectoryPage;
